/**
 * API client + typed wrappers around the FastAPI backend.
 * Uses fetch + TanStack Query in the consumers; vite proxies /api -> :8765.
 */

export class UnauthorizedError extends Error {
  constructor(message = "Non autenticato") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export interface MeResponse {
  username: string;
  display_name: string;
}

// Classificazione unica dell'offerta, calcolata dal backend (services/stato_offerta.py).
export type StatoOfferta =
  | "spenta"
  | "vinta"
  | "recuperabile"
  | "al_limite"
  | "persa_altro"
  | "a_margine"
  | "da_correggere"
  | "senza_floor"
  | "senza_stato";

export interface ProductOut {
  sku: string;
  // null quando il prodotto non ha barcode (colonna nullable dalla 0034)
  ean: string | null;
  name: string | null;
  // Valore GREZZO dal catalogo Poleepo: per Bizzotto contiene il MARCHIO
  // (Yes/Homemotion), non il fornitore. Per mostrare il fornitore usa `supplier`.
  brand: string | null;
  // Fornitore canonico risolto lato server da `brand` (es. "Yes" -> "Bizzotto").
  // Un brand non riconosciuto torna grezzo. null solo se brand e' null.
  supplier: string | null;
  // Marchio commerciale (feed Bizzotto: Yes/Bizzotto/Homemotion/NON DEFINITO),
  // distinto dal fornitore `brand`. Solo "Yes" e' vendibile online.
  vendor_brand: string | null;
  cost: string | null;
  // Netto d'acquisto dal feed Stock-Hub: listino pieno e scontato promo
  // (null = nessuna promo). `cost` e' l'effettivo usato dai calcoli.
  list_cost: string | null;
  discount_cost: string | null;
  shipping_estimate: string | null;
  shipping_cost_certified: string | null;
  shipping_cost_effective: string | null;
  // Fonte di `shipping_cost_effective`: "certified" fatture corriere,
  // "computed" calcolo PIM, "estimate" listino fornitore, "brt" canale estero
  // (FR/ES, stima BRT del listino), "none" manca.
  shipping_cost_source: "certified" | "computed" | "estimate" | "brt" | "none";
  // Costo certificato per-corriere (visibilita'): es. { brt: { cost, n } }.
  // Opzionale: il backend lo popola sempre ({} se vuoto), i mock possono ometterlo.
  shipping_by_courier?: Record<string, { cost: string; n: number }>;
  packaging_cost: string | null;
  floor_price: string | null;
  margin_floor: string | null;
  // Canale price-setter (BricoBravo): prezzo che AM caricherebbe al margine
  // obiettivo. Valorizzati solo se is_price_setter (null/false altrove).
  is_price_setter: boolean;
  target_margin_percent: string | null;
  price_at_target_margin: string | null;
  offer_price: string | null;
  active: boolean;
  // Prezzo deciso a mano: l'auto-prezzo non lo tocca. Vedi isBelowMargin.
  price_locked: boolean;
  marketplace_id: string | null;
  marketplace_label: string | null;
  country: string | null;
  my_price: string | null;
  my_total_price: string | null;
  my_default_price: string | null;
  min_competitor_price: string | null;
  min_competitor_total: string | null;
  min_competitor_shop_id: string | null;
  min_competitor_shop_name: string | null;
  competitor_count: number | null;
  buybox_state: string | null;
  buybox_status_label: string;
  landed_cost: string | null;
  commission_rate: string | null;
  commission_source: string;
  commission_sample_size: number;
  current_fee_cost: string | null;
  current_total_cost: string | null;
  current_margin: string | null;
  current_margin_percent: string | null;
  buybox_target_price: string | null;
  buybox_target_fee_cost: string | null;
  buybox_target_total_cost: string | null;
  buybox_target_margin: string | null;
  buybox_target_margin_percent: string | null;
  recoverable_block_reason: string | null;
  // Classificazione unica: filtri e contatori la leggono, non la ricalcolano.
  stato_offerta: StatoOfferta | null;
  attaccata_al_floor: boolean;
  riapre_sotto_margine: boolean;
  sotto_margine: boolean;
  scostamento_floor_pct: string | null;
  scostamento_floor_eur: string | null;
  scostamento_limite_pct: string | null;
  opportunita: boolean;
  senza_costo: boolean;
  senza_spedizione: boolean;
  prezzo_non_misurato: boolean;
  solo_listino: boolean;
  last_checked_at: string | null;
  image_url: string | null;
  last_poleepo_sync_at: string | null;
}

export interface MarketplaceOut {
  id: string;
  group: string;
  label: string;
  country: string;
  kind: string;
  enabled: boolean;
}

export type MinMarginsMap = Record<string, string>;

export interface MarketplaceAutomation {
  /** mirakl | amazon | poleepo | woocommerce: decide la destinazione della scrittura. */
  kind: string;
  id: string;
  label: string;
  country: string;
  min_margin_pct: string;
  target_margin_pct: string;
  buybox_delta: string;
  auto_apply: boolean;
  is_price_setter: boolean;
  /** Margine applicato quando il fornitore sconta il costo; null = nessun override. */
  promo_margin_pct: string | null;
}

export interface MarketplaceAutomationPatch {
  min_margin_pct?: string;
  target_margin_pct?: string;
  buybox_delta?: string;
  auto_apply?: boolean;
}

export interface SupplierAutomation {
  slug: string;
  label: string;
  auto_apply: boolean;
}

export interface SupplierMargin {
  slug: string;
  label: string;
  /** Override esplicito; null = eredita il margine del canale. */
  margin_pct: string | null;
  /** Valore realmente applicato ai prezzi (override o eredita'). */
  effective_pct: string;
}

export interface SupplierAutomationPatch {
  auto_apply: boolean;
}

export interface ManualRepriceRequest {
  sku: string;
  marketplace_id: string;
  new_price: string;
}

export interface ManualRepriceStage {
  success: boolean;
  error: string;
  status_code: number | null;
}

export interface ManualRepriceResponse {
  poleepo: ManualRepriceStage;
  mirakl: ManualRepriceStage;
  new_price: string;
  auto_sync_triggered: boolean;
}

export interface LookupCacheState {
  found: boolean;
  publication_id: number | null;
  last_synced_at: string | null;
}

export interface LookupLivePublication {
  publication_id: number;
  source_id: string;
  marketplace_id: string | null;
}

export interface LookupLiveError {
  message: string;
}

export interface LookupLiveState {
  requested: boolean;
  pages_scanned: number;
  publications: LookupLivePublication[];
  matched_target: LookupLivePublication | null;
  error: LookupLiveError | null;
}

export type LookupDiagnosis =
  | "ok"
  | "marketplace_not_poleepo_managed"
  | "not_in_cache_run_sync"
  | "found_live_run_sync"
  | "sku_in_poleepo_other_marketplaces"
  | "sku_not_in_poleepo"
  | "live_scan_incomplete"
  | "live_unavailable";

export interface LookupPublicationResponse {
  sku: string;
  marketplace_id: string;
  marketplace_supported_by_poleepo: boolean;
  expected_poleepo_sources: string[];
  cache: LookupCacheState;
  live: LookupLiveState;
  diagnosis: LookupDiagnosis;
  recommendation: string;
}

export interface ActivityItem {
  id: number;
  ts: string;
  kind: string;
  marketplace_id: string | null;
  sku: string | null;
  actor: string;
  success: boolean | null;
  duration_ms: number | null;
  message: string;
  payload: Record<string, unknown>;
}

export interface ActivityListResponse {
  items: ActivityItem[];
  next_cursor: number | null;
}

export interface ActivityListParams {
  kind?: string;
  marketplace_id?: string;
  sku?: string;
  success?: boolean;
  since?: string;
  until?: string;
  cursor?: number;
  limit?: number;
}

export interface PriceChangeItem {
  id: number;
  ts: string;
  sku: string | null;
  marketplace_id: string | null;
  /** 'create' | 'update' | 'manual' | 'failed' | 'skipped' */
  change_type: string;
  /** 'auto' | 'manual' */
  trigger: string;
  /** Prezzo precedente (stringa Decimal o null) */
  old_price: string | null;
  /** Prezzo nuovo (stringa Decimal o null) */
  new_price: string | null;
  success: boolean | null;
  reason: string;
}

export interface PriceChangeListResponse {
  items: PriceChangeItem[];
  next_cursor: number | null;
}

export interface PriceChangeListParams {
  marketplace_id?: string;
  since?: string;
  until?: string;
  include_failed?: boolean;
  include_skipped?: boolean;
  sku?: string;
  success?: boolean;
  change_type?: string;
  cursor?: number;
  limit?: number;
}

export interface MarketplaceSyncStatus {
  marketplace_id: string;
  label: string;
  country: string;
  last_sync_at: string | null;
  fresh: boolean;
}

// Panoramica (GET /api/dashboard): numeri precalcolati ogni ora per canale.
export type TipoCanale = "buybox" | "price_setter";

export interface KpiCanale {
  versione: 1;
  tipo: TipoCanale;
  offerte: number;
  spente: number | null;
  riaprono_sotto_margine: number;
  vinte: number;
  attaccate_al_floor: number;
  recuperabili: number;
  recuperabili_auto: number;
  motivi_blocco: Record<string, number>;
  al_limite: number;
  al_limite_mediana_pct: string | null;
  perse_altro: number;
  senza_stato: number;
  a_margine: number;
  da_correggere: number;
  senza_floor: number;
  sotto_margine: number;
  sotto_margine_mediana_pct: string | null;
  sotto_margine_peggiore_eur: string | null;
  opportunita: number;
  senza_costo: number;
  senza_spedizione: number;
  prezzi_non_misurati: number;
  solo_listino: number;
  dati_mancanti: number;
  controllate_24h: number;
  auto_acceso: boolean;
  scritti_auto: number;
  scritti_manuali: number;
  fallite: number;
  in_attesa: number;
  in_attesa_dal: string | null;
}

export interface PuntoAndamento {
  giorno: string;
  offerte: number;
  vinte: number;
  spente: number | null;
  sotto_margine: number;
  recuperabili: number;
  al_limite: number;
  attaccate_al_floor: number;
  da_correggere: number;
}

export interface CanalePanoramica {
  marketplace_id: string;
  label: string;
  country: string;
  kind: string;
  tipo: TipoCanale;
  calcolato_at: string | null;
  ultimo_errore_at: string | null;
  ultimo_errore: string | null;
  kpi: KpiCanale | null;
  ieri: PuntoAndamento | null;
  andamento: PuntoAndamento[];
}

export interface PanoramicaOut {
  generato_at: string;
  soglia_attaccata_al_floor_pct: string;
  canali: CanalePanoramica[];
}

export interface ApprovalItem {
  sku: string;
  marketplace_id: string;
  current_price: string | null;
  suggested_price: string | null;
  floor_price: string | null;
  delta_pct: string | null;
  value_at_stake_eur: string | null;
  recoverable_margin_eur: string | null;
  critical: boolean;
  suggestion_id: number | null;
}

export interface ApproveResult {
  sku: string;
  marketplace_id: string;
  applied: boolean;
  poleepo_success: boolean;
  mirakl_success: boolean;
  error: string;
}

export interface BulkApproveResult {
  applied: number;
  results: ApproveResult[];
  failed: ApproveResult[];
}

export interface DryRunExample {
  sku: string;
  marketplace_id: string;
  current_price: string | null;
  suggested_price: string | null;
  floor_price: string | null;
  delta_pct: string | null;
  value_at_stake_eur: string | null;
  recoverable_margin_eur: string | null;
  critical: boolean;
}

export interface DryRunResponse {
  marketplace_id: string;
  threshold: string;
  total_skus_evaluated: number;
  auto_applicable_count: number;
  above_threshold_count: number;
  critical_count: number;
  delta_distribution: Record<string, number>;
  examples: DryRunExample[];
}

export interface SettingsBody {
  values: Record<string, string>;
}

export interface ImportReport {
  imported: number;
  skipped: number;
  rows: Array<{ row: number; sku: string; ean: string; accepted: boolean; error: string | null }>;
}

// --- Registro Container -----------------------------------------------------

export interface ContainerMargin {
  price: string | null;
  margin: string | null;
  margin_percent: string | null;
}

export interface ContainerLineOut {
  id: number;
  sku: string;
  name: string | null;
  quantity: number;
  goods_cost: string;
  volume_m3: string | null;
  // Logistica di magazzino per pezzo: sta sulla riga perche' dipende da quanto
  // ingombra l'articolo. I due campi sono alternativi.
  warehouse_flat_per_piece: string | null;
  warehouse_monthly_per_piece: string | null;
  target_price: string | null;
  // Calcolati dal container. null = costo non determinabile: la riga non
  // scrive niente sul prodotto (regola "senza costo nessun prezzo").
  cost: string | null;
  packaging_cost: string | null;
  shipping: string | null;
  // false = sku non ancora a catalogo in AM. La riga resta in attesa.
  in_catalog: boolean;
  clone_sku: string | null;
  owned: boolean;
  margins: Record<string, ContainerMargin>;
}

export interface ContainerOut {
  id: number;
  code: string;
  name: string;
  status: "in_arrivo" | "a_terra" | "chiuso";
  landed_on: string | null;
  months_in_stock: string | null;
  line_count: number;
  total_pieces: number;
  goods_value: string;
  logistics_accrued: string;
  // Cosa chiede attenzione: se e' vuoto, su questo container oggi non c'e'
  // niente da fare. Vedi api/containers._attention.
  attention: string[];
}

export interface ContainerDetailOut extends ContainerOut {
  duty_total: string | null;
  freight_total: string | null;
  landing_total: string | null;
  allocation: "pezzi" | "volume";
  notes: string | null;
  lines: ContainerLineOut[];
}

export interface ContainerLineInput {
  sku: string;
  quantity: number;
  goods_cost: string;
  volume_m3?: string | null;
  warehouse_flat_per_piece?: string | null;
  warehouse_monthly_per_piece?: string | null;
  target_price?: string | null;
}

export interface LandResult {
  applied: string[];
  missing_sku: string[];
  no_cost: string[];
}

export interface CloseResult {
  released: string[];
  handed_over: string[];
  still_locked: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // X-Requested-With marca la richiesta come fatta da JS dello stesso origin.
  // I form HTML cross-origin non possono settare header custom -> protegge
  // i mutation endpoint contro CSRF con basic_auth browser-cached.
  // Vedi security review M1 + middleware/csrf.py.
  const baseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: { ...baseHeaders, ...(init?.headers ?? {}) },
  });
  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  health: () =>
    request<{
      status: string;
      version: string;
      build: string;
      build_number: string;
      build_sha: string;
      build_dirty: boolean;
      started_at: string;
    }>("/health"),
  login: (username: string, password: string, remember: boolean) =>
    request<MeResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password, remember }),
    }),
  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),
  me: () => request<MeResponse>("/auth/me"),
  marketplaces: () => request<MarketplaceOut[]>("/marketplaces"),
  minMargins: () => request<MinMarginsMap>("/marketplaces/min-margins"),
  marketplaceSyncStatus: () => request<MarketplaceSyncStatus[]>("/marketplaces/sync-status"),
  getSavedViewsBackup: () => request<{ payload: string }>("/saved-views"),
  putSavedViewsBackup: (payload: string) =>
    request<{ payload: string }>("/saved-views", {
      method: "PUT",
      body: JSON.stringify({ payload }),
    }),
  setMinMargin: (marketplaceId: string, value: string) =>
    request<{ marketplace_id: string; value: string }>(
      `/marketplaces/${encodeURIComponent(marketplaceId)}/min-margin`,
      { method: "PUT", body: JSON.stringify({ value }) },
    ),
  marketplaceAutomation: () => request<MarketplaceAutomation[]>("/marketplaces/automation"),
  setMarketplaceAutomation: (marketplaceId: string, patch: MarketplaceAutomationPatch) =>
    request<MarketplaceAutomation>(
      `/marketplaces/${encodeURIComponent(marketplaceId)}/automation`,
      { method: "PUT", body: JSON.stringify(patch) },
    ),
  suppliersAutomation: () => request<SupplierAutomation[]>("/marketplaces/suppliers-automation"),
  supplierMargins: (marketplaceId: string) =>
    request<SupplierMargin[]>(
      `/marketplaces/${encodeURIComponent(marketplaceId)}/suppliers-margins`,
    ),
  setSupplierMargin: (marketplaceId: string, slug: string, margin_pct: string | null) =>
    request<SupplierMargin>(
      `/marketplaces/${encodeURIComponent(marketplaceId)}/suppliers/${encodeURIComponent(slug)}/margin`,
      { method: "PUT", body: JSON.stringify({ margin_pct }) },
    ),
  putPromoMargin: (marketplaceId: string, margin_pct: string | null) =>
    request<{ marketplace_id: string; margin_pct: string | null }>(
      `/marketplaces/${encodeURIComponent(marketplaceId)}/promo-margin`,
      { method: "PUT", body: JSON.stringify({ margin_pct }) },
    ),
  setSupplierAutomation: (slug: string, patch: SupplierAutomationPatch) =>
    request<SupplierAutomation>(`/marketplaces/suppliers/${encodeURIComponent(slug)}/automation`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  manualReprice: (input: ManualRepriceRequest) =>
    request<ManualRepriceResponse>("/reprice/manual", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  activityList: (params: ActivityListParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
    const query = qs.toString();
    return request<ActivityListResponse>(`/activity${query ? `?${query}` : ""}`);
  },
  priceChanges: (params: PriceChangeListParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
    const query = qs.toString();
    return request<PriceChangeListResponse>(`/price-changes${query ? `?${query}` : ""}`);
  },
  priceChangesExportUrl: (params: PriceChangeListParams = {}): string => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
    const query = qs.toString();
    return `/api/price-changes/export${query ? `?${query}` : ""}`;
  },
  activityKinds: () => request<string[]>("/activity/kinds"),
  activityDetail: (id: number) => request<ActivityItem>(`/activity/${id}`),
  activityExportUrl: (params: ActivityListParams = {}): string => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
    const query = qs.toString();
    return `/api/activity/export${query ? `?${query}` : ""}`;
  },
  syncPoleepoPublications: () =>
    request<{ status: string; message: string }>("/poleepo/sync-publications", { method: "POST" }),
  importPoleepoCatalog: () =>
    request<{ status: string; message: string }>("/poleepo/import-catalog", { method: "POST" }),
  lookupPoleepoPublication: (
    sku: string,
    marketplaceId: string,
    opts: { pageSize?: number; maxPages?: number } = {},
  ) => {
    const qs = new URLSearchParams({ sku, marketplace_id: marketplaceId });
    if (opts.pageSize !== undefined) qs.set("page_size", String(opts.pageSize));
    if (opts.maxPages !== undefined) qs.set("max_pages", String(opts.maxPages));
    return request<LookupPublicationResponse>(`/poleepo/lookup-publication?${qs.toString()}`);
  },
  listProducts: (params: { brand?: string; active?: boolean; marketplace_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.brand) qs.set("brand", params.brand);
    if (params.active !== undefined) qs.set("active", String(params.active));
    if (params.marketplace_id) qs.set("marketplace_id", params.marketplace_id);
    const query = qs.toString();
    return request<ProductOut[]>(`/products${query ? `?${query}` : ""}`);
  },
  patchProduct: (sku: string, fields: Partial<ProductOut>) =>
    request<ProductOut>(`/products/${encodeURIComponent(sku)}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),
  productThumbnailUrl: (sku: string): string =>
    `/api/products/${encodeURIComponent(sku)}/thumbnail`,
  templateUrl: (fmt: "csv" | "xlsx" = "xlsx"): string => `/api/products/template?fmt=${fmt}`,
  importProducts: async (file: File): Promise<ImportReport> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/products/import", {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: fd,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  exportMarketplaceOffers: async (
    marketplaceId: string,
    skus: string[],
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const res = await fetch("/api/products/marketplace-offers-export", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ marketplace_id: marketplaceId, skus }),
    });
    if (!res.ok) throw new Error(await res.text());
    return {
      blob: await res.blob(),
      filename: filenameFromContentDisposition(res.headers.get("content-disposition")),
    };
  },
  exportMarketplaceMissing: async (
    marketplaceId: string,
    skus: string[],
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const res = await fetch("/api/products/marketplace-missing-export", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ marketplace_id: marketplaceId, skus }),
    });
    if (!res.ok) throw new Error(await res.text());
    return {
      blob: await res.blob(),
      filename: filenameFromContentDisposition(res.headers.get("content-disposition")),
    };
  },
  panoramica: () => request<PanoramicaOut>("/dashboard"),
  containers: () => request<ContainerOut[]>("/containers"),
  container: (code: string) =>
    request<ContainerDetailOut>(`/containers/${encodeURIComponent(code)}`),
  createContainer: (input: { code: string; name: string; landed_on?: string | null }) =>
    request<ContainerDetailOut>("/containers", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  patchContainer: (code: string, patch: Record<string, unknown>) =>
    request<ContainerDetailOut>(`/containers/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  addContainerLine: (code: string, line: ContainerLineInput) =>
    request<ContainerDetailOut>(`/containers/${encodeURIComponent(code)}/lines`, {
      method: "POST",
      body: JSON.stringify(line),
    }),
  patchContainerLine: (code: string, lineId: number, patch: Record<string, unknown>) =>
    request<ContainerDetailOut>(
      `/containers/${encodeURIComponent(code)}/lines/${lineId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),
  deleteContainerLine: (code: string, lineId: number) =>
    request<void>(`/containers/${encodeURIComponent(code)}/lines/${lineId}`, {
      method: "DELETE",
    }),
  landContainer: (code: string) =>
    request<LandResult>(`/containers/${encodeURIComponent(code)}/land`, { method: "POST" }),
  closeContainer: (code: string, successor: string | null) =>
    request<CloseResult>(`/containers/${encodeURIComponent(code)}/close`, {
      method: "POST",
      body: JSON.stringify({ successor }),
    }),
  approvals: (marketplaceId: string) =>
    request<ApprovalItem[]>(`/approvals?marketplace_id=${encodeURIComponent(marketplaceId)}`),
  approveReprice: (sku: string, marketplaceId: string) =>
    request<ApproveResult>("/approvals/approve", {
      method: "POST",
      body: JSON.stringify({ sku, marketplace_id: marketplaceId }),
    }),
  approveBulk: (items: Array<{ sku: string; marketplace_id: string }>) =>
    request<BulkApproveResult>("/approvals/approve-bulk", {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  dryRun: (marketplaceId: string, threshold?: string) => {
    const qs = new URLSearchParams({ marketplace_id: marketplaceId });
    if (threshold !== undefined) qs.set("threshold", threshold);
    return request<DryRunResponse>(`/reprice/dry-run?${qs.toString()}`);
  },
  refreshNow: () => request<{ status: string }>("/refresh/now", { method: "POST" }),
  settings: () => request<SettingsBody>("/settings"),
  putSettings: (values: Record<string, string>) =>
    request<SettingsBody>("/settings", {
      method: "PUT",
      body: JSON.stringify({ values }),
    }),
  storeCredential: (key: string, value: string) =>
    request<void>(`/credentials/${encodeURIComponent(key)}`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
};

function filenameFromContentDisposition(raw: string | null): string | null {
  if (!raw) return null;
  const match = /filename="?([^";]+)"?/i.exec(raw);
  return match?.[1] ?? null;
}
